import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { DefaultTheme } from '@blocksuite/affine-model';
import { ThemeProvider } from '@blocksuite/affine-shared/services';
import { stopPropagation } from '@blocksuite/affine-shared/utils';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  type BlockStdScope,
  PropTypes,
  requiredProperties,
  ShadowlessElement,
  stdContext,
} from '@blocksuite/std';
import { consume } from '@lit/context';
import { computed, effect } from '@preact/signals-core';
import { nothing } from 'lit';
import { property } from 'lit/decorators.js';

import { databaseBackground } from '../database-canvas-block.css';

@requiredProperties({
  database: PropTypes.instanceOf(Object),
})
export class EdgelessDatabaseBackground extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  readonly backgroundStyle$ = computed(() => {
    const themeProvider = this.std.get(ThemeProvider);
    const theme = themeProvider.theme$.value;

    // Resolve the exact default Page/Note card surface in every theme.
    const backgroundColor = themeProvider.generateColorProperty(
      DefaultTheme.noteBackgrounColor,
      DefaultTheme.noteBackgrounColor,
      theme
    );

    // Database styling - default values
    const borderRadius = 12;
    return {
      borderRadius: borderRadius + 'px',
      backgroundColor: backgroundColor,
    };
  });

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(databaseBackground);
    this.disposables.add(
      effect(() => {
        Object.assign(this.style, this.backgroundStyle$.value);
      })
    );
    // Stop propagation to prevent unintended interactions
    this.disposables.addFromEvent(this, 'pointerdown', stopPropagation);
  }

  override render() {
    return nothing;
  }

  @consume({ context: stdContext })
  accessor std!: BlockStdScope;

  @property({ attribute: false })
  accessor editing: boolean = false;

  @property({ attribute: false })
  accessor database!: DatabaseBlockModel;
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-database-background': EdgelessDatabaseBackground;
  }
}
